/**
 * StudioJsonPanel.tsx — Aba JSON do editor do Studio
 *
 * Permite colar um documento de blocos (normalmente gerado por uma LLM a partir
 * do catálogo oficial) e conferir o resultado ANTES de aplicar no modelo.
 *
 * Regras atendidas:
 *   - nada é descartado em silêncio: correções automáticas e perdas estruturais
 *     aparecem separadas na tela;
 *   - com perda estrutural, aplicar exige confirmação explícita;
 *   - a prévia mostrada é o conteúdo JÁ normalizado (o mesmo que será gravado);
 *   - o botão "Copiar modelo atual" gera o exemplo para a IA continuar de onde
 *     o usuário parou.
 *
 * RELEASE: v1.0.0 - 2026-09-10 - Versão inicial (importação de JSON no Studio)
 */

import React, { useState } from 'react';
import { AlertTriangle, CheckCircle2, Copy, FileJson, Loader2, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { InlineNotice } from '@/components/ui/inline-notice';
import { StudioPreview } from '@/components/studio/StudioPreview';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import type { StudioDocument, StudioTemplateType } from '@/lib/studio/core';
import {
  importStudioJson,
  parseStudioJsonDocument,
  serializeStudioDocument,
  StudioJsonImportError,
  type StudioJsonImportResult,
} from '@/lib/studio/import';

interface StudioJsonPanelProps {
  /** Documento aberto no editor (usado no "Copiar modelo atual"). */
  documentoAtual: StudioDocument;
  /** Tipo do modelo aberto (proposta, proposta online, landing page...). */
  tipoDocumento: StudioTemplateType;
  /** Identificador do modelo, para vincular as imagens guardadas. */
  templateId?: string | null;
  /** Somente leitura quando o usuário não tem permissão de importar. */
  podeImportar: boolean;
  /** Aplica o documento normalizado no editor. */
  onAplicar: (documento: StudioDocument) => void;
}

export function StudioJsonPanel({
  documentoAtual,
  tipoDocumento,
  templateId,
  podeImportar,
  onAplicar,
}: StudioJsonPanelProps) {
  const [texto, setTexto] = useState('');
  const [analise, setAnalise] = useState<StudioJsonImportResult | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aceitaPerda, setAceitaPerda] = useState(false);
  const [ocupado, setOcupado] = useState(false);

  /** Lê e normaliza o conteúdo colado, sem alterar o modelo ainda. */
  const conferir = () => {
    setErro(null);
    setAceitaPerda(false);
    try {
      const resultado = parseStudioJsonDocument(texto);
      setAnalise(resultado);
      if (resultado.documentType && resultado.documentType !== tipoDocumento) {
        toast.warning(
          `O arquivo foi criado para outro tipo de modelo (${resultado.documentType}). Confira o resultado antes de aplicar.`,
        );
      }
    } catch (e) {
      setAnalise(null);
      // Erros de formato já têm mensagem própria. Qualquer outra falha mostra a
      // causa real, para não virar um "não foi possível" sem explicação.
      logger.error('studio', 'Falha ao conferir documento JSON', e);
      const detalhe = e instanceof Error && e.message ? ` (${e.message})` : '';
      setErro(
        e instanceof StudioJsonImportError
          ? e.message
          : `Não foi possível ler o conteúdo${detalhe}.`,
      );
    }
  };

  /** Envia ao servidor (para guardar imagens embutidas) e aplica no editor. */
  const aplicar = async () => {
    if (!analise) return;
    setOcupado(true);
    try {
      const resposta = await importStudioJson(JSON.stringify(analise.document), templateId ?? null);
      const documento = JSON.parse(resposta.document) as StudioDocument;
      const guardadas = resposta.report?.savedImages?.length ?? 0;
      onAplicar(documento);
      setTexto('');
      setAnalise(null);
      toast.success(
        guardadas
          ? `Conteúdo aplicado. ${guardadas} imagem(ns) foram guardadas na empresa.`
          : 'Conteúdo aplicado ao modelo.',
      );
    } catch (e) {
      logger.error('studio', 'Falha ao aplicar documento JSON', e);
      toast.error('Não foi possível aplicar o conteúdo.');
    } finally {
      setOcupado(false);
    }
  };

  /** Copia o modelo aberto para servir de exemplo à IA. */
  const copiarAtual = async () => {
    try {
      await navigator.clipboard.writeText(serializeStudioDocument(documentoAtual, tipoDocumento));
      toast.success('Modelo atual copiado.');
    } catch {
      toast.error('Não foi possível copiar.');
    }
  };

  const temPerda = (analise?.losses.length ?? 0) > 0;

  return (
    <div className="grid h-full min-h-0 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      {/* ------------------------------------------------------ Coluna esquerda */}
      <div className="flex min-h-0 flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <FileJson className="h-4 w-4 text-muted-foreground" />
            Colar documento em JSON
          </div>
          <Button variant="outline" size="sm" onClick={copiarAtual}>
            <Copy className="mr-2 h-3.5 w-3.5" />
            Copiar modelo atual
          </Button>
        </div>

        <Textarea
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder='{ "schemaVersion": 1, "documentType": "landing_page", "blocks": [ ... ] }'
          className="min-h-[240px] flex-1 font-mono text-xs"
          disabled={!podeImportar || ocupado}
        />

        {erro && <InlineNotice variant="error">{erro}</InlineNotice>}

        {!podeImportar && (
          <InlineNotice variant="warning">
            Você não tem permissão para importar conteúdo neste modelo.
          </InlineNotice>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={conferir} disabled={!podeImportar || !texto.trim() || ocupado}>
            <Wand2 className="mr-2 h-4 w-4" />
            Conferir conteúdo
          </Button>

          {analise && (
            <Button
              variant="default"
              onClick={aplicar}
              disabled={ocupado || (temPerda && !aceitaPerda)}
            >
              {ocupado ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Aplicar no modelo
            </Button>
          )}
        </div>

        {temPerda && (
          <label className="flex items-start gap-2 text-xs text-muted-foreground">
            <Checkbox
              checked={aceitaPerda}
              onCheckedChange={(v) => setAceitaPerda(v === true)}
              className="mt-0.5"
            />
            Entendo que parte do conteúdo será perdida e quero aplicar mesmo assim.
          </label>
        )}
      </div>

      {/* -------------------------------------------------------- Coluna direita */}
      <div className="flex min-h-0 flex-col gap-3">
        {!analise ? (
          <InlineNotice variant="info">
            Cole o conteúdo e clique em “Conferir” para ver o resultado antes de aplicar.
          </InlineNotice>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Badge variant="secondary">{analise.validBlocks} bloco(s) aproveitado(s)</Badge>
              {analise.kind === 'fragment' && <Badge variant="outline">Trecho de página</Badge>}
              {analise.generator?.name && (
                <Badge variant="outline">Origem: {analise.generator.name}</Badge>
              )}
            </div>

            <ScrollArea className="max-h-44 rounded-md border p-3">
              <ul className="space-y-2 text-xs">
                {analise.losses.map((item, i) => (
                  <li key={`p-${i}`} className="flex gap-2 text-destructive">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>{item.message}</span>
                  </li>
                ))}
                {analise.fixes.map((item, i) => (
                  <li key={`c-${i}`} className="flex gap-2 text-muted-foreground">
                    <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                    <span>{item.message}</span>
                  </li>
                ))}
                {!analise.losses.length && !analise.fixes.length && (
                  <li className="text-muted-foreground">
                    Conteúdo válido — nenhum ajuste foi necessário.
                  </li>
                )}
              </ul>
            </ScrollArea>

            {/* Prévia do conteúdo JÁ normalizado */}
            <StudioPreview document={analise.document} className="min-h-0 flex-1" />
          </>
        )}
      </div>
    </div>
  );
}
